import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { DialogShell } from '../ui/DialogShell';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { comprobanteService } from '../../services/comprobanteService';
import type { ComprobanteReciente, EntidadDinero } from '../../types/comprobante';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';

interface ComprobantesRecientesDialogProps {
  readonly entidad: EntidadDinero;
  readonly onElegir: (comprobante: ComprobanteReciente) => void;
  readonly onClose: () => void;
}

export const ComprobantesRecientesDialog: React.FC<ComprobantesRecientesDialogProps> = ({ entidad, onElegir, onClose }) => {
  const [recientes, setRecientes] = useState<ComprobanteReciente[] | null>(null);
  const [miniaturas, setMiniaturas] = useState<Readonly<Record<string, string>>>({});
  const [error, setError] = useState<string | null>(null);

  const [intento, setIntento] = useState(0);

  // Depende del tipo y el id, no del objeto: el formulario puede armar la entidad en cada render.
  const { tipo: entidadTipo, id: entidadId } = entidad;

  useEffect(() => {
    let vigente = true;
    const cargar = async () => {
      try {
        const lista = await comprobanteService.getRecientes({ tipo: entidadTipo, id: entidadId, nombre: '' });
        if (!vigente) return;
        setRecientes(lista);
        const imagenes = lista.filter((c) => c.tipo_archivo === 'imagen');
        if (imagenes.length > 0) {
          const urls = await comprobanteService.firmarRutas(imagenes.map((c) => c.rutas[0]));
          if (vigente) setMiniaturas(Object.fromEntries(imagenes.map((c, i) => [c.id, urls[i]])));
        }
      } catch (e: unknown) {
        if (!vigente) return;
        setError('No pudimos cargar los comprobantes recientes.');
        reportarErrorInesperado(e, 'No pudimos cargar los comprobantes recientes.');
      }
    };
    void cargar();
    return () => { vigente = false; };
  }, [entidadTipo, entidadId, intento]);

  return (
    <DialogShell isOpen onClose={onClose} size="md" level="nested" title="Usar un comprobante ya subido">
      <FormErrorAlert error={error} action={{ label: 'Reintentar', onClick: () => { setError(null); setIntento((n) => n + 1); } }} />
      {recientes === null && !error && <p role="status" className="text-sm text-slate-500">Cargando…</p>}
      {recientes?.length === 0 && (
        <p className="text-sm text-slate-500">Todavía no hay comprobantes subidos en esta entidad.</p>
      )}
      <ul className="space-y-2">
        {recientes?.map((c) => (
          <li key={c.id}>
            <button type="button" onClick={() => { onElegir(c); }}
              className="w-full min-h-11 flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-left hover:border-slate-300">
              {miniaturas[c.id]
                ? <img src={miniaturas[c.id]} alt="" className="w-12 h-16 object-cover rounded border border-slate-200" />
                : <FileText className="w-8 h-8 text-slate-500 shrink-0" aria-hidden="true" />}
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">Subido el {formatDate(c.created_at)}</span>
                {c.movimientos.map((m) => (
                  <span key={`${m.fecha}-${m.monto}`} className="block text-xs text-slate-500 truncate">
                    Respalda {formatCurrency(m.monto)} · {m.descripcion ?? 'sin descripción'}
                  </span>
                ))}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </DialogShell>
  );
};
